/**
 * Connection Guard Utility
 * Blocks external connections in development for security
 */

interface ConnectionGuardConfig {
  blockExternalConnections: boolean;
  allowedDomains: string[];
  environment: string;
}

/**
 * Get connection guard configuration
 */
function getConnectionGuardConfig(): ConnectionGuardConfig {
  return {
    blockExternalConnections: import.meta.env.VITE_BLOCK_EXTERNAL_CONNECTIONS === 'true',
    allowedDomains: (import.meta.env.VITE_ALLOWED_DOMAINS || 'localhost,127.0.0.1').split(','),
    environment: import.meta.env.VITE_APP_ENVIRONMENT || 'development',
  };
}

/**
 * Check if a URL is allowed
 */
export function isUrlAllowed(url: string): boolean {
  const config = getConnectionGuardConfig();
  
  // In production, allow all connections
  if (config.environment === 'production') {
    return true;
  }
  
  // If blocking is disabled, allow all connections
  if (!config.blockExternalConnections) {
    return true;
  }
  
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname;
    
    // Check if hostname is in allowed list
    return config.allowedDomains.some(domain => {
      // Exact match
      if (hostname === domain) {
        return true;
      }
      
      // Subdomain match (e.g., api.localhost)
      if (hostname.endsWith(`.${domain}`)) {
        return true;
      }
      
      return false;
    });
  } catch (error) {
    // If URL parsing fails, block it
    console.error('Invalid URL:', url);
    return false;
  }
}

/**
 * Block external connection and throw error
 */
export function blockExternalConnection(url: string): never {
  const error = new Error(
    `🔒 External connection blocked in development: ${url}\n` +
    `Allowed domains: ${getConnectionGuardConfig().allowedDomains.join(', ')}\n` +
    `To allow this connection, add the domain to VITE_ALLOWED_DOMAINS in .env.development`
  );
  
  console.error(error.message);
  throw error;
}

/**
 * Validate URL before making request
 */
export function validateConnection(url: string): void {
  if (!isUrlAllowed(url)) {
    blockExternalConnection(url);
  }
}

/**
 * Check if connection guard is active
 */
export function isConnectionGuardActive(): boolean {
  const config = getConnectionGuardConfig();
  return config.blockExternalConnections && config.environment !== 'production';
}

/**
 * Get allowed domains list
 */
export function getAllowedDomains(): string[] {
  return getConnectionGuardConfig().allowedDomains;
}

/**
 * Log connection guard status
 */
export function logConnectionGuardStatus(): void {
  const config = getConnectionGuardConfig();
  
  if (config.blockExternalConnections && config.environment !== 'production') {
    console.log('🔒 Connection Guard Active');
    console.log('   Environment:', config.environment);
    console.log('   Allowed domains:', config.allowedDomains.join(', '));
    console.log('   External connections will be blocked');
  } else {
    console.log('🌐 Connection Guard Inactive');
    console.log('   Environment:', config.environment);
    console.log('   All connections allowed');
  }
}
